import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { QuizForm } from '@/components/ui/quiz/QuizForm';
import { CheckSquare, FileText } from 'lucide-react';
import { ContentData, QuizData } from './types';
import TextEditor from './TextEditor';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ContentEditorProps {
  initialContent?: ContentData;
  initialQuiz?: QuizData;
  onSave: (content: ContentData, quiz: QuizData) => void;
  className?: string;
  isSaving?: boolean;
  lessonTitle?: string;
  onTitleChange?: (title: string) => void;
  courseName?: string;
}

const ContentEditor: React.FC<ContentEditorProps> = ({
  initialContent = { type: 'text', content: '' },
  initialQuiz = { questions: [] },
  onSave,
  className,
  isSaving = false,
  lessonTitle = '',
  onTitleChange = () => {},
  courseName = 'Course',
}) => {
  // Always use 'text' type, keeping code simpler
  const [content, setContent] = useState<ContentData>({ 
    type: 'text', 
    content: initialContent.content 
  });
  const [quiz, setQuiz] = useState<QuizData>(initialQuiz);
  const [activeTab, setActiveTab] = useState<string>('content');

  const handleContentChange = (value: string) => {
    setContent({ type: 'text', content: value });
  };

  const handleQuizChange = (newQuiz: QuizData) => {
    setQuiz(newQuiz);
  };

  const handleSave = () => {
    onSave(content, quiz);
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div className="mb-4">
        <div className="flex flex-col space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="lessonTitle" className="text-sm font-medium">
              Lesson Title
            </Label>
            <span className="text-sm text-muted-foreground">
              Course: {courseName}
            </span>
          </div>
          <Input
            id="lessonTitle"
            type="text"
            value={lessonTitle}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Enter lesson title"
            className="w-1/2"
          />
        </div>
      </div>

      <Tabs 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="content" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span>Content</span>
          </TabsTrigger>
          <TabsTrigger value="quiz" className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4" />
            <span>Quiz</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="space-y-6 pt-4 animate-fade-in">
          <div className="space-y-4">
            <TextEditor 
              content={content.content}
              onChange={handleContentChange}
            />
          </div>
        </TabsContent>

        <TabsContent value="quiz" className="animate-fade-in">
          <QuizForm
            initialQuiz={quiz}
            onChange={handleQuizChange}
          />
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={handleSave} size="lg" disabled={isSaving}>
          {isSaving ? (
            <>
              <span className="h-4 w-4 mr-2 animate-spin rounded-full border-b-2 border-white"></span>
              Saving...
            </>
          ) : 'Save Lesson'}
        </Button>
      </div>
    </div>
  );
};

export default ContentEditor;
